import React from 'react'
import { Drawer } from '@/components/ui/Drawer'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Layers } from 'lucide-react'
import type { MrpPlanningRow } from '../types/mrpTypes'
import { formatSpecDisplay, formatDemandTypeLabel, formatNumberVi } from '../utils/mrpFormatters'

export interface MrpTraceDrawerProps {
  open: boolean
  onClose: () => void
  row: MrpPlanningRow | null
}

export function MrpTraceDrawer({
  open,
  onClose,
  row,
}: MrpTraceDrawerProps): React.ReactElement | null {
  if (!row) return null

  const specText = formatSpecDisplay(row.material_spec)
  const traces = row.demand_traces || []

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Truy vết nguồn nhu cầu MRP"
      widthClass="w-full max-w-2xl lg:max-w-3xl"
    >
      <div className="space-y-5 text-xs">
        {/* Header summary of selected material */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-bold text-slate-800">
                {row.material_name}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-2 text-slate-500">
                <span>
                  ĐVT: <strong className="text-slate-700">{row.uom}</strong>
                </span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-[11px] text-slate-500 block">Thiếu cần mua</span>
              <span
                className={`text-base font-bold tabular-nums ${
                  row.shortage_qty > 0 ? 'text-rose-700' : 'text-emerald-700'
                }`}
              >
                {formatNumberVi(row.shortage_qty)} {row.uom}
              </span>
            </div>
          </div>

          {specText !== '—' && (
            <div className="pt-2 border-t border-slate-200 flex items-center gap-2">
              <span className="text-slate-500 font-medium">Quy cách:</span>
              <span className="font-mono text-slate-800 bg-white px-2 py-0.5 rounded border border-slate-200">
                {specText}
              </span>
            </div>
          )}
        </div>

        {/* Quantities summary breakdown (authoritative from server) */}
        <div className="grid grid-cols-4 gap-2 rounded border border-slate-200 bg-white p-3 text-center">
          <div>
            <div className="text-[11px] text-slate-500">Tổng nhu cầu</div>
            <div className="mt-0.5 font-bold tabular-nums text-slate-800">
              {formatNumberVi(row.required_qty)}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-slate-500">Đã giữ hàng</div>
            <div className="mt-0.5 font-semibold tabular-nums text-blue-700">
              {formatNumberVi(row.reserved_qty)}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-slate-500">Khả dụng tự do</div>
            <div className="mt-0.5 font-semibold tabular-nums text-emerald-700">
              {formatNumberVi(row.free_unreserved_qty)}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-slate-500">PO đang về</div>
            <div className="mt-0.5 font-semibold tabular-nums text-indigo-700">
              {formatNumberVi(row.incoming_po_qty)}
            </div>
          </div>
        </div>

        {/* Trace List */}
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-700">
            <span>Danh sách nguồn nhu cầu chi tiết ({traces.length})</span>
          </div>

          {traces.length === 0 ? (
            <div className="rounded border border-dashed border-slate-200 p-6 text-center text-slate-400">
              Không có dữ liệu truy vết nguồn nhu cầu cho dòng này.
            </div>
          ) : (
            traces.map((trace, idx) => (
              <div
                key={trace.requirement_line_id || idx}
                className="rounded-lg border border-slate-200 bg-white p-3 shadow-xs space-y-2.5"
              >
                {/* Demand header */}
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2">
                  <div className="flex items-center gap-2">
                    <Badge tone="info" className="font-medium">
                      {formatDemandTypeLabel(trace.demand_type)}
                    </Badge>
                    <span className="font-semibold text-slate-800">
                      {trace.requirement_batch_title || `Đợt nhu cầu ${trace.requirement_batch_id}`}
                    </span>
                  </div>
                  {trace.shortage_qty != null && trace.shortage_qty > 0 && (
                    <span className="rounded bg-rose-50 px-2 py-0.5 font-semibold text-rose-700 border border-rose-200">
                      Thiếu tại dòng: {formatNumberVi(trace.shortage_qty)} {row.uom}
                    </span>
                  )}
                </div>

                {/* Line References */}
                <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-500 bg-slate-50/60 p-2 rounded">
                  <div>
                    <span className="text-slate-400">Đợt nhu cầu ID: </span>
                    <span className="font-mono text-slate-700">{trace.requirement_batch_id}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Dòng nhu cầu ID: </span>
                    <span className="font-mono text-slate-700">{trace.requirement_line_id}</span>
                  </div>
                  {trace.plan_line_id && (
                    <div>
                      <span className="text-slate-400">Dòng kế hoạch sản xuất: </span>
                      <span className="font-mono font-medium text-blue-700">
                        {trace.plan_line_id}
                      </span>
                    </div>
                  )}
                  {trace.plan_order_code && (
                    <div>
                      <span className="text-slate-400">Mã lệnh sản xuất: </span>
                      <span className="font-semibold text-slate-700">
                        {trace.plan_order_code}
                      </span>
                    </div>
                  )}
                </div>

                {/* Product contributions table */}
                {trace.contributions && trace.contributions.length > 0 && (
                  <div className="space-y-1.5 pt-1">
                    <div className="text-[11px] font-semibold text-slate-600 flex items-center gap-1.5">
                      <Layers className="h-3 w-3 text-slate-400" />
                      Cấu thành từ sản phẩm / thành phẩm:
                    </div>
                    <div className="overflow-x-auto rounded border border-slate-200">
                      <table className="min-w-full divide-y divide-slate-200 text-left text-[11px]">
                        <thead className="bg-slate-50 text-slate-600">
                          <tr>
                            <th className="px-2.5 py-1.5 w-10 text-center">STT</th>
                            <th className="px-2.5 py-1.5">Mã sản phẩm</th>
                            <th className="px-2.5 py-1.5">Tên sản phẩm</th>
                            <th className="px-2.5 py-1.5 text-right">SL đóng góp</th>
                            <th className="px-2.5 py-1.5 text-center">Nguồn BOM</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {trace.contributions.map((c, cIdx) => (
                            <tr key={c.source_bom_line_id || cIdx} className="hover:bg-slate-50/50">
                              <td className="px-2.5 py-1.5 text-center text-slate-400 tabular-nums">
                                {cIdx + 1}
                              </td>
                              <td className="px-2.5 py-1.5 font-mono font-medium text-slate-800">
                                {c.product_code}
                              </td>
                              <td className="px-2.5 py-1.5 text-slate-600">
                                {c.product_name || '—'}
                              </td>
                              <td className="px-2.5 py-1.5 text-right tabular-nums font-semibold text-slate-800">
                                {formatNumberVi(c.qty)}
                              </td>
                              <td className="px-2.5 py-1.5 text-center text-slate-400">
                                {c.bom_source || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end pt-3 border-t border-slate-200">
          <Button size="sm" variant="secondary" onClick={onClose}>
            Đóng
          </Button>
        </div>
      </div>
    </Drawer>
  )
}
